import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { ArrowRight, CircuitBoard } from "lucide-react";

export default function Login() {
  const { user, login, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [companyLogo, setCompanyLogo] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${api.defaults.baseURL}/branding`).then(async (r) => {
      try {
        const data = await r.json();
        setCompanyLogo(data?.logo_data_b64 || "");
      } catch (err) {
        console.warn("[branding] respuesta no JSON:", err);
      }
    }).catch((err) => console.warn("[branding] no se pudo cargar:", err));
  }, []);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result?.ok) {
      if (result.user?.must_change_password) navigate("/change-password");
      else navigate("/");
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2" style={{ background: "var(--bg)" }}>
      {/* LEFT · Form column */}
      <div className="flex flex-col px-8 lg:px-16 py-10">
        <div className="flex items-center gap-3">
          {companyLogo ? (
            <img src={companyLogo} alt="Logo" className="w-12 h-12 object-contain rounded-lg" data-testid="login-brand-logo" />
          ) : (
            <div
              className="w-10 h-10 grid place-items-center rounded-lg text-white font-display font-black"
              style={{ background: "linear-gradient(160deg,#6E8B3D,#8AAF4E)", boxShadow: "inset 0 -2px 0 rgba(0,0,0,.15),0 1px 2px rgba(15,18,16,.08)" }}
            >
              <CircuitBoard size={22} strokeWidth={2.2} />
            </div>
          )}
          <div>
            <div className="font-display font-black text-[13px] leading-tight" data-testid="login-brand-name">
              Software de Gestión de reparaciones
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[.14em] text-[#3F5222] mt-1 font-semibold">
              iRepairs.es
            </div>
          </div>
        </div>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md">
            <div className="ds-card ds-hero-glow p-8" style={{ boxShadow: "var(--shadow-lg)" }}>
              <div className="font-mono text-[11px] uppercase tracking-[.14em] text-[#3F5222] font-semibold">
                <span className="text-[#8A9098]">//</span>&nbsp;&nbsp;Acceso al sistema
              </div>
              <h1 className="font-display text-4xl md:text-5xl font-black mt-2 leading-[1.05] tracking-tight">
                Bienvenido.
              </h1>
              <p className="text-[#4B5158] mt-3 text-sm">
                Sistema cerrado. Solo administradores pueden crear nuevos usuarios.
              </p>

              <form onSubmit={submit} className="mt-7 space-y-4">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[.14em] text-[#8A9098] font-semibold">
                    Email
                  </label>
                  <input
                    data-testid="login-email"
                    required
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    className="mt-1 ds-input"
                    style={{ padding: "10px 12px" }}
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[.14em] text-[#8A9098] font-semibold">
                    Contraseña
                  </label>
                  <input
                    data-testid="login-password"
                    required
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    className="mt-1 ds-input"
                    style={{ padding: "10px 12px" }}
                  />
                </div>

                {error && (
                  <div
                    data-testid="login-error"
                    className="text-xs bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded-lg font-mono"
                  >
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  data-testid="login-submit"
                  disabled={loading}
                  className="ds-btn primary w-full justify-center"
                  style={{ padding: "12px 16px", fontSize: 14 }}
                >
                  {loading ? "Comprobando..." : "Iniciar sesión"}
                  <ArrowRight size={15} />
                </button>
              </form>

              <div className="mt-6 pt-4 border-t border-dashed border-[#D5D7CE] text-xs text-[#4B5158]">
                ¿Necesitas una cuenta? Contacta con tu administrador.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT · Hero photo column */}
      <div className="hidden lg:block relative" style={{ background: "#0F1210" }}>
        <img
          src="/login-bg.jpg"
          alt="Taller de reparación"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/30 to-black/70" />
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-5">
            <span className="w-2 h-2 rounded-full bg-[#8AAF4E]" style={{ boxShadow: "0 0 0 3px rgba(138,175,78,.25)" }}/>
            <span className="font-mono text-[10px] uppercase tracking-[.16em] text-white/90 font-semibold">
              Workshop OS
            </span>
          </div>
          <div className="font-display font-black text-4xl leading-tight tracking-tight">
            Control total del taller.
            <br />
            <span className="text-white/70">Desde la recepción hasta la entrega.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
